import React, { useCallback, useId, useMemo, useState } from 'react';
import { match } from 'ts-pattern';

import { TextInput } from '@fragment/ui/src/components/Input/TextInput';
import { LoadingText } from '@fragment/ui/src/components/LoadingText';
import { Table } from '@fragment/ui/src/components/Table';
import { TextInputCopy } from '@fragment/ui/src/components/TextInputCopy/TextInputCopy';

import type { McpClientNode } from '../../../mcpClient/types';
import { useGetWorkspaceMcpClientsQuery } from '../../../../hooks/internalApi';
import { useInternalApiContext } from '../../../../hooks/useInternalApiContext';
import { getResultOrError } from '../../../../utils/errors';
import {
  useWorkspace,
  useWorkspaceId,
} from '../../../../wrappers/WorkspaceProvider';
import { DeleteMcpClientForm } from '../../../mcpClient/DeleteMcpClientForm';
import { TableLoadError } from '../../../TableLoadError/TableLoadError';
import { getWorkspaceMcpUrl } from './mcpUrl';
import { SettingsModalLayout } from './SettingsModalLayout';

const schema = [
  {
    title: 'Name',
    key: 'name',
  },
  {
    title: 'ID',
    key: 'id',
    justify: 'right',
  },
] as const;

const emptyValue = {
  ID: 'N/A',
  Name: 'No connected MCP clients yet',
};

const WorkspaceMcpUrl = () => {
  const fieldId = useId();
  const workspaceId = useWorkspaceId();
  const { workspace: activeWorkspaceFromContext } = useWorkspace();
  if (!activeWorkspaceFromContext?.pod) return null;
  return (
    <TextInputCopy
      id={fieldId}
      label="MCP Server URL"
      size="md"
      width="w-full"
      padding="p-0"
      border={false}
      value={getWorkspaceMcpUrl(
        activeWorkspaceFromContext.pod.customerApiUrl,
        workspaceId
      )}
    />
  );
};

const McpClientDetail = ({
  mcpClient,
  onConfirmedDelete,
}: {
  mcpClient: McpClientNode;
  onConfirmedDelete: VoidFunction;
}) => {
  const namespace = useId();

  return (
    <div className="space-y-f4">
      <div className="space-y-f2">
        <TextInput
          flexContainer="space-y-f0"
          id={`${namespace}-client-id`}
          label="Client ID"
          padding="p-0"
          value={mcpClient.id}
          size="md"
          width="w-full"
          readOnly
        />
        <WorkspaceMcpUrl />
      </div>
      <DeleteMcpClientForm mcpClient={mcpClient} onDelete={onConfirmedDelete} />
    </div>
  );
};

export const McpClientsSettingsForm = ({
  hideHeader = false,
}: {
  hideHeader?: boolean;
} = {}) => {
  const workspaceId = useWorkspaceId();
  const { context } = useInternalApiContext();
  const variables = useMemo(() => ({ workspaceId }), [workspaceId]);
  const [{ data, error, fetching }, refetchMcpClients] =
    useGetWorkspaceMcpClientsQuery({
      variables,
      context,
      pause: !context.token,
    });
  const { isError: isWorkspaceError, result: workspace } = getResultOrError(
    data?.workspace,
    error
  );
  const { isError: isMcpClientsError, result: mcpClients } = getResultOrError(
    workspace?.mcpClients,
    error
  );

  const [selectedClient, setSelectedClient] = useState<
    McpClientNode | undefined
  >(undefined);

  const refresh = useCallback(
    () => refetchMcpClients({ requestPolicy: 'network-only' }),
    [refetchMcpClients]
  );

  const onRowClick = useCallback(
    (client: McpClientNode) => setSelectedClient(client),
    []
  );
  const isError = isWorkspaceError || isMcpClientsError;
  // No data yet and no error means the first response is still in flight (or
  // the query is paused awaiting a token) — a loading state, not an error.
  const isLoading = !data && !isError;

  const onCancel = useCallback(() => setSelectedClient(undefined), []);

  const onConfirmedDelete = useCallback(() => {
    setSelectedClient(undefined);
    refresh();
  }, [refresh]);

  const viewState: 'list' | 'error' | 'loading' | 'detail' = isError
    ? 'error'
    : match([selectedClient, isLoading] as const)
        .with([undefined, true], () => 'loading' as const)
        .with([undefined, false], () => 'list' as const)
        .otherwise(() => 'detail' as const);

  return (
    <SettingsModalLayout
      hideHeader={hideHeader}
      title={match(viewState)
        .with('detail', () => 'MCP Client')
        .with('list', () => 'MCP Clients')
        .with('loading', () => 'MCP Clients')
        .with('error', () => 'Failed to load MCP Clients')
        .exhaustive()}
      onBackButtonClick={viewState === 'detail' ? onCancel : undefined}
    >
      {match(viewState)
        .with('error', () => (
          <TableLoadError
            entityColumnName="Name"
            rightColumnName="ID"
            {...{ error, retry: refresh, fetching }}
          />
        ))
        .with('loading', () => <LoadingText text="Loading" />)
        .with('list', () => (
          <div className="space-y-f4">
            <div className="space-y-f2">
              <WorkspaceMcpUrl />
              <p className="text-main-500">
                Paste this URL into your MCP host (Claude Code, Claude Desktop,
                Cursor, …). It will open the dashboard to authorize the
                connection; connected clients appear below and can be revoked by
                deleting them.
              </p>
            </div>
            <Table
              textSize="md"
              data={mcpClients?.nodes ?? []}
              onRowClick={onRowClick}
              schema={schema}
              emptyValue={emptyValue}
              borderedRows
            />
          </div>
        ))
        .with('detail', () => (
          <McpClientDetail
            mcpClient={selectedClient!}
            onConfirmedDelete={onConfirmedDelete}
          />
        ))
        .exhaustive()}
    </SettingsModalLayout>
  );
};
